// In-memory DataSource used by the /dev mount (and as the fallback for screens
// the live API cannot serve yet — see STATIC_BACKED in ./types).
//
// It is a real little database: mutations stick, so upsert -> query -> compact
// -> delete all behave the way the console will once the endpoints exist. The
// scenario switch reseeds it, which is how every empty state in the mockup is
// reachable in the browser.

import type {
  Analytics, ApiKey, Billing, CompactionEntry, ConsoleSettings, DocumentRecord,
  EmbedderProfile, HealthTile, Job, Member, MultiQueryOutcome, Namespace,
  NamespaceDraft, Operation, OperationsSummary, QueryHit, QueryOutcome, Repo,
  TrashEntry, UpsertAck, Workspace, WorkspaceSummary,
} from "../types/domain";
import type {
  DataSource, DocumentDraft, InviteDraft, KeyDraft, MultiQueryInput, QueryInput,
  RepoDraft, WorkspaceDraft,
} from "./types";
import {
  DEFAULT_WORKSPACE, GENERIC_CORPUS, SEED_BILLING, SEED_COMPACTION, SEED_CORPUS,
  SEED_HEALTH, SEED_JOBS, SEED_KEYS, SEED_MEMBERS, SEED_NAMESPACES,
  SEED_OPERATIONS, SEED_PROFILES, SEED_REPOS, SEED_SETTINGS, SEED_TRASH,
  SEED_WORKSPACES, seedAnalytics,
} from "./seed";

export type Scenario = "seeded" | "empty" | "fresh-workspace";

export const SCENARIOS: { key: Scenario; label: string; hint: string }[] = [
  { key: "seeded", label: "seeded", hint: "the mockup's populated console" },
  { key: "empty", label: "empty", hint: "brand-new workspace — every empty state" },
  { key: "fresh-workspace", label: "fresh ws", hint: "namespaces exist but no traffic yet" },
];

interface Store {
  scenario: Scenario;
  workspaces: Workspace[];
  namespaces: Namespace[];
  documents: Map<string, DocumentRecord[]>;
  operations: Operation[];
  jobs: Job[];
  repos: Repo[];
  members: Member[];
  keys: ApiKey[];
  trash: TrashEntry[];
  compaction: CompactionEntry[];
  settings: ConsoleSettings;
  billing: Billing;
  health: HealthTile[];
  allWorkspaces: WorkspaceSummary[];
}

// Deterministic id generation — no Math.random, so /dev screenshots are stable
// across reloads and can be diffed against the mockup.
let counter = 0;
function nextId(prefix: string, width = 8): string {
  counter += 1;
  const hex = (counter * 2654435761 >>> 0).toString(16).padStart(8, "0");
  return prefix + hex.slice(0, width);
}

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

function buildStore(scenario: Scenario): Store {
  const empty = scenario === "empty";
  const fresh = scenario === "fresh-workspace";

  const namespaces: Namespace[] = empty
    ? []
    : clone(SEED_NAMESPACES).map((n) =>
        fresh
          ? { ...n, vectors: 0, deltas: 0, tombstones: 0, sizeBytes: 0, searches: 0, p95: 0, cacheHit: 0, errors: 0, baseVer: 0, status: "ready" as const, updated: "just now" }
          : n,
      );

  const documents = new Map<string, DocumentRecord[]>();
  if (!empty && !fresh) {
    for (const [ns, docs] of Object.entries(SEED_CORPUS)) {
      documents.set(ns, docs.map(({ score: _score, ...d }) => d));
    }
  }

  return {
    scenario,
    workspaces: [{ id: DEFAULT_WORKSPACE, name: DEFAULT_WORKSPACE, role: "owner" }],
    namespaces,
    documents,
    operations: empty || fresh ? [] : clone(SEED_OPERATIONS),
    jobs: empty || fresh ? [] : clone(SEED_JOBS),
    repos: empty ? [] : clone(SEED_REPOS),
    members: empty ? [SEED_MEMBERS[0]] : clone(SEED_MEMBERS),
    keys: empty ? [] : clone(SEED_KEYS),
    trash: empty || fresh ? [] : clone(SEED_TRASH),
    compaction: empty || fresh ? [] : clone(SEED_COMPACTION),
    settings: clone(SEED_SETTINGS),
    billing: clone(SEED_BILLING),
    health: empty
      ? [
          { key: "serving nodes", value: "3 / 3 up", tone: "ok", detail: "vf-server · Cloud Run" },
          { key: "ingest log", value: "healthy", tone: "ok", detail: "cloudpubsub · 0 dead-letter" },
          { key: "L2 cache", value: "connected", tone: "ok", detail: "kvrocks · no traffic" },
          { key: "embed service", value: "ready", tone: "ok", detail: "gemini · idle" },
        ]
      : clone(SEED_HEALTH),
    allWorkspaces: empty ? [{ id: DEFAULT_WORKSPACE, members: 1, namespaces: 0, vectors: "0", searches: "0", plan: "free" }] : clone(SEED_WORKSPACES),
  };
}

let store = buildStore("seeded");

/** Latency envelope so loading states are exercised in /dev. */
let latencyMs = 180;

function delay<T>(value: T, ms = latencyMs): Promise<T> {
  if (ms <= 0) return Promise.resolve(value);
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

export function setScenario(scenario: Scenario): void {
  store = buildStore(scenario);
  counter = 0;
}

export function currentScenario(): Scenario {
  return store.scenario;
}

export function setLatency(ms: number): void {
  latencyMs = ms;
}

function corpusFor(namespace: string): (DocumentRecord & { score: number })[] {
  const seeded = SEED_CORPUS[namespace];
  if (seeded) return seeded;
  const live = store.documents.get(namespace);
  if (live?.length) {
    return live.map((d, i) => ({ ...d, score: Math.max(0.35, 0.9 - i * 0.043) }));
  }
  return store.namespaces.some((n) => n.id === namespace) && store.scenario !== "empty"
    ? GENERIC_CORPUS
    : [];
}

function nsLabel(id: string): string {
  return store.namespaces.find((n) => n.id === id)?.label ?? id;
}

function touch(namespace: string, addedDocs: number): void {
  const ns = store.namespaces.find((n) => n.id === namespace);
  if (!ns) return;
  ns.vectors += addedDocs;
  ns.deltas += 1;
  ns.updated = "just now";
}

export const staticSource: DataSource = {
  kind: "static",

  // ---- workspaces -------------------------------------------------------
  listWorkspaces: () => delay(clone(store.workspaces)),

  createWorkspace: (draft: WorkspaceDraft) => {
    const id =
      draft.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") ||
      nextId("ws-", 4);
    const ws: Workspace = { id, name: draft.name.trim() || id, role: "owner" };
    store.workspaces = [ws, ...store.workspaces];
    store.allWorkspaces = [
      { id, members: 1, namespaces: 0, vectors: "0", searches: "0", plan: "free" },
      ...store.allWorkspaces,
    ];
    return delay(ws);
  },

  listMembers: () => delay(clone(store.members)),

  inviteMember: (_workspaceId: string, draft: InviteDraft) => {
    const name = draft.email.split("@")[0];
    const member: Member = {
      uid: nextId("uid_", 4),
      name,
      email: draft.email.trim(),
      role: draft.role,
      lastActive: "invite pending",
      initials: name.slice(0, 2).toUpperCase(),
    };
    store.members = [member, ...store.members];
    return delay(member);
  },

  // ---- namespaces -------------------------------------------------------
  listNamespaces: () => delay(clone(store.namespaces)),

  getNamespace: (id: string) => delay(clone(store.namespaces.find((n) => n.id === id) ?? null)),

  createNamespace: (workspaceId: string, draft: NamespaceDraft) => {
    const id = `${workspaceId}/${draft.name.trim()}`;
    const ns: Namespace = {
      id,
      label: draft.name.trim(),
      kind: draft.bm25 && draft.dims === 0 ? "BM25-only" : "Knowledge base",
      metric: draft.metric,
      dims: draft.dims,
      profile: draft.profile,
      vectors: 0, deltas: 0, tombstones: 0, sizeBytes: 0,
      updated: "just now", searches: 0, p95: 0, cacheHit: 0, errors: 0,
      bm25: draft.bm25, status: "indexing", baseVer: 0,
    };
    store.namespaces = [ns, ...store.namespaces];
    store.documents.set(id, []);
    return delay(ns);
  },

  renameNamespace: (id: string, name: string) => {
    const ns = store.namespaces.find((n) => n.id === id);
    if (ns) ns.label = name;
    return delay(undefined);
  },

  deleteNamespace: (id: string) => {
    const ns = store.namespaces.find((n) => n.id === id);
    store.namespaces = store.namespaces.filter((n) => n.id !== id);
    store.documents.delete(id);
    store.compaction = store.compaction.filter((c) => c.namespace !== id);
    if (ns) {
      store.trash = [
        { namespace: ns.label, size: `${Math.max(1, Math.round(ns.sizeBytes / (1024 * 1024)))} MB`, expires: "in 7 days" },
        ...store.trash,
      ];
    }
    return delay(undefined);
  },

  compactNamespace: (id: string) => {
    const entry = store.compaction.find((c) => c.namespace === id);
    if (entry) entry.status = "running";
    const ns = store.namespaces.find((n) => n.id === id);
    if (ns) { ns.deltas = 0; ns.baseVer += 1; ns.tombstones = 0; }
    store.jobs = [
      { id: nextId("job_", 4), kind: "compact", namespace: id, status: "queued", started: "just now", duration: "—", note: "awaiting worker slot" },
      ...store.jobs,
    ];
    return delay(undefined);
  },

  // ---- documents --------------------------------------------------------
  listDocuments: (namespace: string) => {
    const live = store.documents.get(namespace);
    if (live) return delay(clone(live));
    const seeded = SEED_CORPUS[namespace];
    return delay(seeded ? seeded.map(({ score: _s, ...d }) => d) : []);
  },

  upsertDocument: (namespace: string, draft: DocumentDraft) => {
    const id = draft.id.trim() || nextId("doc_", 6);
    const metadata: Record<string, unknown> = { _source: "upsert" };
    for (const [k, v] of Object.entries(draft.metadata)) {
      if (k.trim()) metadata[k.trim()] = v;
    }
    const doc: DocumentRecord = {
      id,
      content: draft.content.trim() || "(no content provided)",
      metadata,
      isNew: true,
      vector: draft.embed === "server" ? undefined : [0.021, -0.114, 0.307, 0.058, -0.229],
    };
    const existing = store.documents.get(namespace) ?? [];
    store.documents.set(namespace, [doc, ...existing.filter((d) => d.id !== id)]);
    touch(namespace, 1);

    const operationId = nextId("op_", 8);
    const op: Operation = {
      id: operationId, namespace, kind: "upsert", docs: 1, status: "queued",
      age: "just now", attempt: 1, seq: "—", pieces: 1,
    };
    store.operations = [op, ...store.operations];
    // The owner drains asynchronously; flip to committed the way the real
    // pipeline does so the 202 -> poll -> committed flow is exercised.
    setTimeout(() => {
      const live = store.operations.find((o) => o.id === operationId);
      if (live) { live.status = "committed"; live.seq = store.operations.length; }
    }, 800);

    return delay<UpsertAck>({ operationId, status: "queued" });
  },

  deleteDocument: (namespace: string, id: string) => {
    const existing = store.documents.get(namespace) ?? [];
    store.documents.set(namespace, existing.filter((d) => d.id !== id));
    const ns = store.namespaces.find((n) => n.id === namespace);
    if (ns) { ns.vectors = Math.max(0, ns.vectors - 1); ns.tombstones += 1; ns.updated = "just now"; }
    return delay(undefined);
  },

  getOperation: (_namespace: string, operationId: string) =>
    delay(clone(store.operations.find((o) => o.id === operationId) ?? null), 60),

  // ---- query ------------------------------------------------------------
  query: (input: QueryInput) => {
    const corpus = corpusFor(input.namespace);
    const hits: QueryHit[] = corpus.slice(0, input.topK).map((d) => ({
      id: d.id, score: d.score, content: d.content, metadata: d.metadata,
    }));
    const request = {
      query_text: input.text,
      top_k: input.topK,
      ...(input.hybrid ? { hybrid: { bm25_text: input.text } } : {}),
      include_content: true,
      include_metadata: true,
    };
    const response = {
      results: hits.map((h) => ({
        id: h.id,
        score: Number(h.score.toFixed(4)),
        metadata: h.metadata,
        content: h.content.length > 96 ? `${h.content.slice(0, 96)}…` : h.content,
      })),
      query_time_ms: 18 + input.topK * 2,
    };
    return delay<QueryOutcome>(
      {
        hits,
        queryTimeMs: 18 + input.topK * 2,
        request,
        response,
        responseStatus: "200 OK",
      },
      640,
    );
  },

  multiQuery: (input: MultiQueryInput) => {
    const merged: QueryHit[] = [];
    for (const id of input.namespaces) {
      for (const d of corpusFor(id).slice(0, 3)) {
        merged.push({
          id: d.id, score: d.score, content: d.content, metadata: d.metadata,
          namespace: id, namespaceLabel: nsLabel(id),
        });
      }
    }
    merged.sort((a, b) => b.score - a.score);
    return delay<MultiQueryOutcome>(
      {
        hits: merged.slice(0, input.topK),
        queryTimeMs: 26 + input.namespaces.length * 4,
        searchedNamespaces: input.namespaces.length,
      },
      720,
    );
  },

  // ---- pipeline ---------------------------------------------------------
  listOperations: () => delay(clone(store.operations)),

  operationsSummary: () =>
    delay<OperationsSummary>({
      // "in flight" is everything not yet in a terminal state.
      inFlight: store.operations.filter((o) => o.status === "running" || o.status === "queued").length,
      queued: store.operations.filter((o) => o.status === "queued").length,
      deadLetter: store.operations.filter((o) => o.status === "failed").length,
      backend: store.settings.ingestBackend,
    }),

  listJobs: () => delay(clone(store.jobs)),

  cancelJob: (id: string) => {
    const job = store.jobs.find((j) => j.id === id);
    if (job) { job.status = "canceled"; job.note = "canceled by operator"; }
    return delay(undefined);
  },

  compactionQueue: () => delay(clone(store.compaction)),

  listTrash: () => delay(clone(store.trash)),

  restoreTrash: (namespace: string) => {
    store.trash = store.trash.filter((t) => t.namespace !== namespace);
    return delay(undefined);
  },

  purgeTrash: (namespace: string) => {
    store.trash = store.trash.filter((t) => t.namespace !== namespace);
    return delay(undefined);
  },

  // ---- code search ------------------------------------------------------
  listRepos: () => delay(clone(store.repos)),

  connectRepo: (_workspaceId: string, draft: RepoDraft) => {
    const name = draft.fullName.trim();
    const repo: Repo = {
      id: `github.com/${name}`,
      namespace: `github.com/${name}:${draft.defaultBranch}`,
      name,
      defaultBranch: draft.defaultBranch,
      branches: [draft.defaultBranch],
      webhook: draft.webhook ? "healthy" : "stale",
      vectors: 0,
      lastIndexed: "just now",
      status: "indexing",
    };
    store.repos = [repo, ...store.repos];
    store.jobs = [
      { id: nextId("job_", 4), kind: "reindex", namespace: `${repo.id}:${draft.defaultBranch}`, status: "queued", started: "just now", duration: "—", note: "clone pending" },
      ...store.jobs,
    ];
    return delay(repo);
  },

  reindexRepo: (namespace: string) => {
    const repo = store.repos.find((r) => r.namespace === namespace);
    if (repo) repo.status = "indexing";
    store.jobs = [
      { id: nextId("job_", 4), kind: "reindex", namespace, status: "queued", started: "just now", duration: "—", note: "reindex requested" },
      ...store.jobs,
    ];
    return delay(undefined);
  },

  repairWebhook: (namespace: string) => {
    const repo = store.repos.find((r) => r.namespace === namespace);
    if (repo) repo.webhook = "healthy";
    return delay(undefined);
  },

  renameRepo: (id: string, name: string) => {
    const repo = store.repos.find((r) => r.id === id);
    if (repo) repo.name = name;
    return delay(undefined);
  },

  // ---- access -----------------------------------------------------------
  listKeys: () => delay(clone(store.keys)),

  createKey: (workspaceId: string, draft: KeyDraft) => {
    const secret = `vf_live_${nextId("", 8)}${nextId("", 8)}${nextId("", 8)}${nextId("", 8)}${nextId("", 8)}`;
    const collections = draft.collections.length > 0 ? draft.collections : ["*"];
    const key: ApiKey = {
      id: nextId("k_", 4),
      name: draft.name.trim() || "new-service-key",
      masked: `vf_live_••••••••${secret.slice(-4)}`,
      scopes: draft.access === "read_write" ? ["docs:read", "docs:write"] : ["docs:read"],
      grants: collections.map((c) => `${workspaceId}/${c}`).join(", "),
      created: "just now",
      calls: "0",
    };
    store.keys = [key, ...store.keys];
    return delay({ key, secret });
  },

  rotateKey: (workspaceId: string, id: string) => {
    const secret = `vf_live_${nextId("", 8)}${nextId("", 8)}${nextId("", 8)}${nextId("", 8)}${nextId("", 8)}`;
    const masked = `vf_live_••••••••${secret.slice(-4)}`;
    const existing = store.keys.find((k) => k.id === id);
    if (existing) {
      existing.masked = masked;
      return delay({ key: clone(existing), secret });
    }
    // Rotating a key this store has never seen — the live console delegating a
    // real key id here. Answer with a plausible record rather than crashing.
    return delay({
      key: {
        id, name: id, masked, scopes: ["docs:read", "docs:write"],
        grants: `${workspaceId}/*`, created: "—", calls: "—",
      },
      secret,
    });
  },

  revokeKey: (_workspaceId: string, id: string) => {
    store.keys = store.keys.filter((k) => k.id !== id);
    return delay(undefined);
  },

  renameKey: (_workspaceId: string, id: string, name: string) => {
    const key = store.keys.find((k) => k.id === id);
    if (key) key.name = name;
    return delay(undefined);
  },

  listProfiles: (): Promise<EmbedderProfile[]> => delay(clone(SEED_PROFILES)),

  // ---- observability ----------------------------------------------------
  dashboardHealth: () => delay(clone(store.health)),

  analytics: (): Promise<Analytics> => {
    if (store.scenario !== "seeded") {
      return delay<Analytics>({
        searchesTotal: 0, p50Ms: null, p95Ms: null, errorRate: null,
        searchesSeries: new Array(24).fill(0),
        p95Series: new Array(24).fill(0),
        cache: { l1: 0, l2: 0, miss: 0 },
        namespaces: store.namespaces.map((n) => ({
          id: n.id, label: n.label, searches: 0, p95: 0, cacheHit: 0, errors: 0,
        })),
      });
    }
    return delay(seedAnalytics(store.namespaces));
  },

  billing: () => delay(clone(store.billing)),

  // ---- admin ------------------------------------------------------------
  listAllWorkspaces: () => delay(clone(store.allWorkspaces)),

  getSettings: (workspaceId: string) =>
    delay({ ...clone(store.settings), workspaceName: store.settings.workspaceName || workspaceId }),

  saveSettings: (_workspaceId: string, patch: Partial<ConsoleSettings>) => {
    store.settings = { ...store.settings, ...patch };
    return delay(clone(store.settings));
  },
};
